import { useEffect, useMemo, useState, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { CheckCircle, Calendar, Mail, UserPlus, User } from "lucide-react";
import { Button } from "../components/ui/Button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/Card";
import { Badge } from "../components/ui/Badge";
import { useCheckoutStore } from "../stores/checkout-store";
import { useCompleteCheckout } from "../lib/basket-api";
import { fetchClient, useUser } from "../lib/api-client";
import type { CheckoutItem } from "../lib/types";
import { handleApiError } from "../lib/error-handling";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../lib/firebase";
import toast from "react-hot-toast";
import { format, parseISO, isSameDay } from "date-fns";

export const CheckoutSuccess = () => {
  const [searchParams] = useSearchParams();
  const checkoutId = searchParams.get("checkoutId");
  const { clearCheckout } = useCheckoutStore();
  const { isAuthenticated } = useUser();
  const [completed, setCompleted] = useState(false);
  const [password, setPassword] = useState("");
  const [isSigningUp, setIsSigningUp] = useState(false);
  const [isSignedUp, setIsSignedUp] = useState(false);

  // Selection State
  const [selectedParticipantId, setSelectedParticipantId] = useState<string>("");
  const [isSomeoneElse, setIsSomeoneElse] = useState(false);
  const [manualName, setManualName] = useState({ firstName: "", lastName: "" });
  const [selectedSubUserParticipantIds, setSelectedSubUserParticipantIds] = useState<string[]>([]);

  // Complete checkout (submits and returns success data)
  const completeCheckoutMutation = useCompleteCheckout();
  const {
    data,
    isPending: isLoading,
    error,
    mutateAsync: completeCheckoutAsync,
  } = completeCheckoutMutation;

  // Track if we've already initiated completion to prevent duplicate calls
  const hasInitiatedCompletion = useRef(false);
  const [localData, setLocalData] = useState<typeof data>(undefined);
  const [localError, setLocalError] = useState<unknown>(null);

  // Use localData if mutation data isn't available yet
  const displayData = data || localData;
  const checkoutData = displayData?.checkout;
  // Use localError if mutation error isn't available yet
  const displayError = error || localError;

  const depositInfo = useMemo(() => {
    if (!checkoutData?.items?.length) return null;

    let totalDeposit = 0;
    let anyDepositInFuture = false;
    const balancesByDate = new Map<string, number>();
    const now = new Date();

    checkoutData.items.forEach((item: CheckoutItem) => {
      const depositCents = item.depositCents ?? null;
      const balanceDueAt = item.balanceDueAt
        ? parseISO(item.balanceDueAt)
        : null;

      if (
        depositCents !== null &&
        depositCents !== undefined &&
        balanceDueAt &&
        balanceDueAt > now
      ) {
        totalDeposit += depositCents;
        anyDepositInFuture = true;

        const balanceAmount = item.unitPriceCents - depositCents;
        if (balanceAmount > 0) {
          const dateKey = balanceDueAt.toISOString();
          balancesByDate.set(
            dateKey,
            (balancesByDate.get(dateKey) || 0) + balanceAmount,
          );
        }
      } else {
        totalDeposit += item.unitPriceCents;
      }
    });

    if (!anyDepositInFuture) return null;

    const sortedBalances = Array.from(balancesByDate.entries())
      .map(([dateStr, amount]) => ({
        date: parseISO(dateStr),
        amount,
      }))
      .sort((a, b) => a.date.getTime() - b.date.getTime());

    return {
      totalDeposit,
      balances: sortedBalances,
    };
  }, [checkoutData]);

  const hasPayment = Boolean(checkoutData?.totalCents && checkoutData.totalCents > 0);

  // Extract unique participants for selection (one entry per distinct name)
  const uniqueParticipantsList = useMemo(() => {
    if (!displayData) return [];
    const participantsMap = new Map<
      string,
      { id: string; name: string; firstName: string; lastName: string }
    >();
    
    displayData.participants.forEach((p) => {
      const name = `${p.firstName} ${p.lastName}`;
      if (!participantsMap.has(name)) {
        participantsMap.set(name, {
          id: p.id,
          name,
          firstName: p.firstName,
          lastName: p.lastName,
        });
      }
    });

    // Also include crew from race items
    if (displayData.checkout?.items) {
      displayData.checkout.items.forEach((item) => {
        if (item.type === "RACE" && item.raceItem?.crew) {
          item.raceItem.crew.forEach((c) => {
            const name = `${c.firstName} ${c.lastName}`;
            if (!participantsMap.has(name)) {
              participantsMap.set(name, {
                id: c.id || Math.random().toString(),
                name,
                firstName: c.firstName,
                lastName: c.lastName,
              });
            }
          });
        }
      });
    }

    return Array.from(participantsMap.values());
  }, [displayData]);

  const subUserCandidates = useMemo(() => {
    if (!displayData) return [];
    // Only exclude primary when we have a clear primary identity, so the list
    // doesn't disappear while the user is typing "Someone else" name
    let primaryName = "";
    if (selectedParticipantId) {
      primaryName = uniqueParticipantsList.find(p => p.id === selectedParticipantId)?.name ?? "";
    } else if (isSomeoneElse && manualName.firstName.trim() && manualName.lastName.trim()) {
      primaryName = `${manualName.firstName.trim()} ${manualName.lastName.trim()}`;
    }
    return uniqueParticipantsList.filter(p => p.name !== primaryName);
  }, [displayData, uniqueParticipantsList, selectedParticipantId, isSomeoneElse, manualName]);

  const primaryDisplayName = useMemo(() => {
    if (selectedParticipantId) {
      return uniqueParticipantsList.find(p => p.id === selectedParticipantId)?.name ?? "";
    }
    if (isSomeoneElse) {
      const name = `${manualName.firstName} ${manualName.lastName}`.trim();
      return name || "—";
    }
    return "";
  }, [uniqueParticipantsList, selectedParticipantId, isSomeoneElse, manualName]);

  useEffect(() => {
    // Auto-complete checkout on mount if not already completed
    // Use ref to prevent duplicate calls (e.g., from React Strict Mode)
    if (checkoutId && !completed && !isLoading && !data && !localData && !error && !localError && !hasInitiatedCompletion.current) {
      hasInitiatedCompletion.current = true;
      completeCheckoutAsync({
        params: { path: { checkoutId } },
      })
        .then((responseData) => {
          setCompleted(true);
          setLocalData(responseData);
        })
        .catch((err) => {
          setLocalError(err);
          // Reset the ref so user can retry if needed
          hasInitiatedCompletion.current = false;
        });
    }
  }, [checkoutId, completed, isLoading, data, localData, error, localError, completeCheckoutAsync]);

  useEffect(() => {
    // Clear checkout ID from local storage since booking is complete
    if (displayData) {
      clearCheckout();
    }
  }, [displayData, clearCheckout]);

  const onSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayData || !checkoutId || isSigningUp) return;

    const email = displayData.checkout.bookingEmailAddress;

    if (!email) {
      toast.error("No email address found for booking");
      return;
    }

    if (!isSomeoneElse && !selectedParticipantId) {
      toast.error("Please select who is creating this account");
      return;
    }

    if (isSomeoneElse && (!manualName.firstName || !manualName.lastName)) {
      toast.error("Please enter your name");
      return;
    }

    setIsSigningUp(true);
    try {
      const primary = isSomeoneElse
        ? { firstName: manualName.firstName, lastName: manualName.lastName }
        : (() => {
            const selected = uniqueParticipantsList.find(
              (p) => p.id === selectedParticipantId,
            );
            if (!selected) throw new Error("Selected participant not found");
            return {
              firstName: selected.firstName,
              lastName: selected.lastName,
            };
          })();
      const primaryFullName = `${primary.firstName} ${primary.lastName}`.trim();
      const subUserNames = selectedSubUserParticipantIds
        .map((id) => uniqueParticipantsList.find((p) => p.id === id))
        .filter(
          (p): p is { id: string; name: string; firstName: string; lastName: string } =>
            p != null && `${p.firstName} ${p.lastName}`.trim() !== primaryFullName,
        )
        .map((p) => ({ firstName: p.firstName, lastName: p.lastName }));

      // 1. Call our API to create the user and link checkout (cookie proves ownership)
      const { error: signupError } = await fetchClient.POST(
        "/api/public/checkouts/{checkoutId}/signup",
        {
          params: { path: { checkoutId } },
          body: { password, primary, subUserNames },
        },
      );

      if (signupError) {
        const apiError = handleApiError(signupError);
        throw new Error(apiError.message || "Signup failed");
      }

      // 2. Auto-login via Firebase SDK
      await signInWithEmailAndPassword(auth, email, password);
      
      setIsSignedUp(true);
      toast.success("Account created successfully!");
    } catch (err) {
      const apiError = handleApiError(err);
      toast.error(apiError.message || "Failed to create account");
    } finally {
      setIsSigningUp(false);
    }
  };

  const formatPrice = (cents: number, currency: string) => {
    const amount = cents / 100;
    return currency === "gbp"
      ? `£${amount.toFixed(2)}`
      : `${amount.toFixed(2)} ${currency.toUpperCase()}`;
  };

  const formatDate = (dateString: string) => {
    return format(parseISO(dateString), "EEEE, MMMM do, yyyy");
  };

  const formatTime = (dateString: string) => {
    return format(parseISO(dateString), "h:mm a");
  };

  if (!checkoutId) {
    return (
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="text-center py-12">
          <p className="text-red-600">
            Invalid checkout session. No checkout ID provided.
          </p>
          <Button asChild className="mt-4">
            <a href="/events">Browse Events</a>
          </Button>
        </div>
      </div>
    );
  }

  if (error) {
    const apiError = handleApiError(error);
    return (
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="text-center py-12">
          <p className="text-red-600">
            {apiError.message || "Error completing booking. Please try again or contact support."}
          </p>
          <Button asChild className="mt-4">
            <a href="/events">Browse Events</a>
          </Button>
        </div>
      </div>
    );
  }

  // Show loading state only if we don't have data yet and we're still loading
  // Prioritize displayData over isLoading since we store it in localData
  if (!displayData && !displayError && (isLoading || (!displayError && hasInitiatedCompletion.current))) {
    return (
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Completing your booking...</p>
          <p className="text-sm text-gray-500 mt-2">
            Please wait while we process your booking
          </p>
        </div>
      </div>
    );
  }

  // If we have an error or no data after completion attempt, show error
  if (!displayData) {
    const apiError = displayError ? handleApiError(displayError) : null;
    const isForbidden = apiError?.status === 403;
    
    return (
      <div className="max-w-4xl mx-auto space-y-8">
        <div className="text-center py-12">
          {isForbidden ? (
            <>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">
                Booking Not Found
              </h2>
              <p className="text-gray-600 mb-2">
                We couldn't find the booking details you're looking for. This may happen if the booking session has expired or the link is no longer valid.
              </p>
              <p className="text-sm text-gray-500 mb-6">
                If you completed your booking, you should have received a confirmation email with your booking details. If you need assistance, please contact support.
              </p>
            </>
          ) : (
            <p className="text-red-600">
              {apiError?.message || "Unable to load booking details. Please try refreshing the page or contact support if the problem persists."}
            </p>
          )}
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-6">
            <Button asChild>
              <a href="/events">Browse Events</a>
            </Button>
            {isForbidden && (
              <Button variant="outline" asChild>
                <a href="/">Return to Home</a>
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  const { checkout, events } = displayData;

  return (
    <div className="max-w-4xl mx-auto space-y-8">
      {/* Success Header */}
      <div className="text-center">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircle className="w-8 h-8 text-green-600" />
          </div>
        </div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Booking Successful!
        </h1>
        <p className="text-gray-600 text-lg">
          Your booking has been submitted and is pending approval.
        </p>
      </div>

      {/* Status Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle>Booking Status</CardTitle>
            <Badge variant="warning">Pending Approval</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center gap-3">
              <Calendar className="w-5 h-5 text-gray-400" />
              <div>
                <p className="font-medium">Booking Reference</p>
                <p className="text-sm text-gray-600">
                  {checkout.bookingReference || checkout.id}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="w-5 h-5 text-gray-400" />
              <div>
                <p className="font-medium">Confirmation Email</p>
                <p className="text-sm text-gray-600">
                  {checkout.bookingEmailAddress
                    ? `A confirmation email has been sent to ${checkout.bookingEmailAddress}`
                    : "A confirmation email has been sent to your registered email address."}
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Guest Signup Card */}
      {!isAuthenticated && !isSignedUp && (
        <Card className="border-primary-200 bg-primary-50">
          <CardHeader>
            <CardTitle className="text-primary-900 flex items-center gap-2">
              <UserPlus className="w-5 h-5" />
              Save your details for next time?
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <p className="text-sm text-primary-800">
                Create an account using <strong>{checkout.bookingEmailAddress}</strong> to manage your bookings and save your consent information for future events.
              </p>
              
              <form onSubmit={onSignup} className="space-y-4">
                <div className="space-y-3">
                  <label className="text-sm font-medium text-gray-700">Who is creating this account?</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {uniqueParticipantsList.map((p) => (
                      <button
                        key={p.id}
                        type="button"
                        onClick={() => {
                          setSelectedParticipantId(p.id);
                          setIsSomeoneElse(false);
                          // Clear other inputs when switching
                          setSelectedSubUserParticipantIds([]);
                          setManualName({ firstName: "", lastName: "" });
                          setPassword("");
                        }}
                        className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                          selectedParticipantId === p.id && !isSomeoneElse
                            ? "bg-primary-100 border-primary-500 text-primary-900"
                            : "bg-white border-gray-200 hover:border-gray-300 text-gray-700"
                        }`}
                      >
                        <User className={`w-4 h-4 ${selectedParticipantId === p.id && !isSomeoneElse ? "text-primary-600" : "text-gray-400"}`} />
                        <span className="text-sm font-medium">{p.name}</span>
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => {
                        setIsSomeoneElse(true);
                        setSelectedParticipantId("");
                        // Clear other inputs when switching
                        setSelectedSubUserParticipantIds([]);
                        setManualName({ firstName: "", lastName: "" });
                        setPassword("");
                      }}
                      className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
                        isSomeoneElse
                          ? "bg-primary-100 border-primary-500 text-primary-900"
                          : "bg-white border-gray-200 hover:border-gray-300 text-gray-700"
                      }`}
                    >
                      <UserPlus className={`w-4 h-4 ${isSomeoneElse ? "text-primary-600" : "text-gray-400"}`} />
                      <span className="text-sm font-medium">Someone else</span>
                    </button>
                  </div>
                </div>

                {(selectedParticipantId || isSomeoneElse) && (
                  <>
                    {isSomeoneElse && (
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 p-4 bg-white rounded-lg border border-gray-200">
                        <div className="space-y-1">
                          <label className="text-xs font-medium text-gray-500">
                            First Name
                          </label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                            value={manualName.firstName}
                            onChange={(e) =>
                              setManualName({
                                ...manualName,
                                firstName: e.target.value,
                              })
                            }
                            required={isSomeoneElse}
                          />
                        </div>
                        <div className="space-y-1">
                          <label className="text-xs font-medium text-gray-500">
                            Last Name
                          </label>
                          <input
                            type="text"
                            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                            value={manualName.lastName}
                            onChange={(e) =>
                              setManualName({
                                ...manualName,
                                lastName: e.target.value,
                              })
                            }
                            required={isSomeoneElse}
                          />
                        </div>
                      </div>
                    )}

                    <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                      <p className="text-xs font-medium text-gray-500 mb-0.5">Primary account holder</p>
                      <p className="text-sm font-medium text-gray-900">{primaryDisplayName}</p>
                    </div>

                    <div className="space-y-3 p-4 bg-white rounded-lg border border-gray-200">
                      <label className="text-sm font-medium text-gray-700">
                        Save other guests to your account?
                      </label>
                      <p className="text-xs text-gray-500">
                        Selected guests will be saved as sub-users so you can
                        easily book for them again.
                      </p>
                      {subUserCandidates.length > 0 ? (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                          {subUserCandidates.map((p) => (
                            <label
                              key={p.id}
                              className={`flex items-center gap-3 p-2 rounded-md border cursor-pointer transition-colors ${
                                selectedSubUserParticipantIds.includes(p.id)
                                  ? "bg-primary-50 border-primary-300 text-primary-900"
                                  : "bg-white border-gray-200 hover:border-gray-300 text-gray-700"
                              }`}
                            >
                              <input
                                type="checkbox"
                                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                                checked={selectedSubUserParticipantIds.includes(p.id)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setSelectedSubUserParticipantIds([
                                      ...selectedSubUserParticipantIds,
                                      p.id,
                                    ]);
                                  } else {
                                    setSelectedSubUserParticipantIds(
                                      selectedSubUserParticipantIds.filter(
                                        (id) => id !== p.id,
                                      ),
                                    );
                                  }
                                }}
                              />
                              <span className="text-sm">{p.name}</span>
                            </label>
                          ))}
                        </div>
                      ) : (
                        <p className="text-sm text-gray-500">No other guests in this booking.</p>
                      )}
                    </div>

                    <div className="flex flex-col sm:flex-row gap-3 pt-2">
                      <div className="flex-1 space-y-1">
                        <label className="text-xs font-medium text-gray-500">
                          Choose a password
                        </label>
                        <input
                          type="password"
                          placeholder="At least 6 characters"
                          className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 text-sm"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          minLength={6}
                          required
                          disabled={isSigningUp}
                        />
                      </div>
                      <div className="flex items-end">
                        <Button
                          type="submit"
                          disabled={
                            isSigningUp ||
                            (!selectedParticipantId && !isSomeoneElse)
                          }
                          className="w-full sm:w-auto bg-primary-600 hover:bg-primary-700 text-white h-10"
                        >
                          {isSigningUp
                            ? "Creating Account..."
                            : "Create Account"}
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </form>
            </div>
          </CardContent>
        </Card>
      )}

      {isSignedUp && (
        <Card className="border-green-200 bg-green-50">
          <CardContent className="py-4">
            <p className="text-sm text-green-800 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" />
              Account created and logged in! You can now manage your bookings in your profile.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Booking Details */}
      <Card>
        <CardHeader>
          <CardTitle>Booking Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {events.map((event) => {
              const allAttendees = event.participants || [];
              const raceItemsForEvent = checkout.items.filter(
                (item) => item.type === "RACE" && item.raceItem?.eventId === event.id
              );
              
              return (
              <div
                key={event.id}
                className="border border-gray-200 rounded-lg p-4"
              >
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-semibold">{event.title}</h3>
                  <Badge variant="info">
                    {allAttendees.length} attendee
                    {allAttendees.length !== 1 ? "s" : ""}
                  </Badge>
                </div>

                {event.startsAt && event.endsAt && (
                  <div className="space-y-2 mb-4">
                    {(() => {
                      const startDate = parseISO(event.startsAt);
                      const endDate = parseISO(event.endsAt);
                      const isMultiDay = !isSameDay(startDate, endDate);

                      if (isMultiDay) {
                        // Multi-day: show full date range with times
                        return (
                          <>
                            <p className="text-gray-600">
                              <Calendar className="w-4 h-4 inline mr-2" />
                              {format(startDate, "EEEE, MMMM do, yyyy")} •{" "}
                              {format(startDate, "h:mm a")} - {format(endDate, "EEEE, MMMM do, yyyy")} •{" "}
                              {format(endDate, "h:mm a")}
                            </p>
                          </>
                        );
                      } else {
                        // Single-day: show date and time range
                        return (
                          <>
                            <p className="text-gray-600">
                              <Calendar className="w-4 h-4 inline mr-2" />
                              {formatDate(event.startsAt)}
                            </p>
                            <p className="text-gray-600">
                              {formatTime(event.startsAt)} - {formatTime(event.endsAt)}
                            </p>
                          </>
                        );
                      }
                    })()}
                  </div>
                )}

                {raceItemsForEvent.length > 0 && (
                  <div className="mb-4">
                    <h4 className="font-medium mb-2">Boats:</h4>
                    <div className="space-y-1">
                      {raceItemsForEvent.map((item, index) => (
                        <p key={index} className="text-sm text-gray-600">
                          {item.raceItem?.boatClass} - {item.raceItem?.sailNumber}
                        </p>
                      ))}
                    </div>
                  </div>
                )}

                <div>
                  <h4 className="font-medium mb-2">Attendees:</h4>
                  <div className="space-y-1">
                    {allAttendees.map((participant, index) => (
                      <p key={index} className="text-sm text-gray-600">
                        {participant.firstName} {participant.lastName}
                      </p>
                    ))}
                  </div>
                </div>
              </div>
            )})}
          </div>
        </CardContent>
      </Card>

      {/* Payment Summary - only when there is a payment */}
      {hasPayment && (
        <Card>
          <CardHeader>
            <CardTitle>Payment Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Amount</span>
                <span className="font-semibold text-lg">
                  {formatPrice(checkout.totalCents, checkout.currency ?? "gbp")}
                </span>
              </div>

              {depositInfo && (
                <div className="pt-4 border-t border-dashed space-y-2 text-sm">
                  <div className="flex justify-between text-gray-900 font-medium">
                    <span>Due now</span>
                    <span>
                      {formatPrice(depositInfo.totalDeposit, checkout.currency ?? "gbp")}
                    </span>
                  </div>
                  {depositInfo.balances.map((balance, idx) => (
                    <div key={idx} className="flex justify-between text-gray-600">
                      <span>Due on {format(balance.date, "MMM do, yyyy")}</span>
                      <span>{formatPrice(balance.amount, checkout.currency ?? "gbp")}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="pt-4 border-t border-gray-100 flex justify-between text-sm text-gray-600">
                <span>Payment Status</span>
                <span className="font-medium">Setup Intent Created</span>
              </div>
              <div className="flex justify-between text-sm text-gray-600">
                <span>Charging</span>
                <span className="font-medium">
                  {depositInfo ? "Partial payment on approval" : "Full payment on approval"}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Next Steps */}
      <Card>
        <CardHeader>
          <CardTitle>What Happens Next?</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-semibold text-primary-600">
                  1
                </span>
              </div>
              <div>
                <p className="font-medium">Review & Approval</p>
                <p className="text-sm text-gray-600">
                  Our team will review your booking and contact you within 2-3
                  business days.
                </p>
              </div>
            </div>

            {hasPayment && (
              <div className="flex items-start gap-3">
                <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                  <span className="text-xs font-semibold text-primary-600">
                    2
                  </span>
                </div>
                <div>
                  <p className="font-medium">Payment Processing</p>
                  <p className="text-sm text-gray-600">
                    Once approved, your payment will be processed automatically.
                  </p>
                </div>
              </div>
            )}

            <div className="flex items-start gap-3">
              <div className="w-6 h-6 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                <span className="text-xs font-semibold text-primary-600">
                  {hasPayment ? "3" : "2"}
                </span>
              </div>
              <div>
                <p className="font-medium">Event Confirmation</p>
                <p className="text-sm text-gray-600">
                  You'll receive final confirmation and event details via email.
                </p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <Button asChild>
          <a href="/events">Browse More Events</a>
        </Button>
        <Button variant="outline" asChild>
          <a href="/">Return to Home</a>
        </Button>
      </div>
    </div>
  );
};
